import { reactive } from 'vue'
import { defineStore } from 'pinia'
import { toast } from 'vue-sonner'
import { useUser } from '@/composables/useUser'
import { Execution } from '@/shared/Executions/models/Execution'
import { GetExecutionsArgs } from '@/shared/Executions/models/GetExecutionsArgs'
import { InjectionService } from '@/shared/services/InjectionService'
import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
import { AdvancedAnalysisPhaseType, ExecutionPhaseType } from '@/shared/Executions/models/ExecutionPhaseType'
import { ExecutionWorkflowType } from '@/shared/Executions/models/ExecutionWorkflowType'
import { ExecutionApiService } from '@/shared/Executions/services/ExecutionApiService'
import { GetExecutionsResponse } from '@/shared/Executions/models/GetExecutionsResponse'
import { GetExecutionsViewType } from '@/shared/Executions/models/GetExecutionsViewType'
import { storeRegistry } from '@/store/storeRegistry'


const DEFAULT_PAGE_LIMIT = 50;

/**
 * Factory function that creates a Pinia store instance for the given workflow type.
 * Each workflow type gets its own isolated store with a unique ID: `executionHistory-${workflowType}`.
 */
export function createExecutionHistoryStore(workflowType: ExecutionWorkflowType, executionApiService: ExecutionApiService) {
    return defineStore(`executionHistory-${workflowType}`, () => {

        const initialState = {
            executions: new Array<Execution>(),

            // Active filters
            executionStatusType: [] as ExecutionStatusType[],
            executionType: [] as Array<ExecutionPhaseType | typeof AdvancedAnalysisPhaseType>,
            limit: DEFAULT_PAGE_LIMIT as number,
            startDate: undefined as string | undefined,
            endDate: undefined as string | undefined,
            numberDays: undefined as number | undefined,
            viewType: GetExecutionsViewType.Flat,
            folderId: undefined as string | undefined,
            // Loading state
            isLoading: false,
            isLoadingMore: false,
            isRefreshing: false,
            hasLoadingError: false,
            paginationError: false,
            lastFetch: 0,
        }

        const executionHistory = reactive({ ...initialState })

        // Incremented on every loadExecutionHistory call. Background pagination
        // checks this against the value captured at the time it was started, and
        // self-cancels when they no longer match.
        let activeLoadId = 0;

        // Guards against the DynamoDB edge case where the last page echoes back
        // the same nextKey that was used to request it.
        const seenKeys = new Set<string>();

        // Persistent map from execution id → reactive proxy inside executionHistory.executions.
        // Avoids rebuilding an O(n) Map from scratch on every background refresh.
        // IMPORTANT: values must be reactive proxies obtained via executionHistory.executions[i],
        // not raw objects, so Object.assign updates flow through Vue's reactivity system.
        const executionMap = new Map<string, Execution>();

        function resetStore() {
            Object.assign(executionHistory, {
                ...initialState,
                executions: new Array<Execution>()
            });
            activeLoadId++;   // cancels any in-flight background pagination
            seenKeys.clear();
            executionMap.clear();
        }

        storeRegistry.register(`executionHistory-${workflowType}`, resetStore)

        async function buildArgs(): Promise<GetExecutionsArgs> {
            const auth = useUser();
            await auth.initialize();
            const args = new GetExecutionsArgs({
                userId: auth.user.id as string,
                tenantId: auth.user.tenant_id as string,
                executionWorkflowType: workflowType,
                startDate: executionHistory.startDate,
                endDate: executionHistory.endDate,
            });
            args.limit = executionHistory.limit;
            args.viewType = executionHistory.viewType;
            if (executionHistory.folderId) {
                args.folderId = executionHistory.folderId;
            }
            return args;
        }

        /**
         * Merges `incoming` records into the existing executions array:
         * - Existing records are updated in-place via Object.assign (preserves
         *   object identity so Vue does not unmount/remount table rows).
         * - New records not yet in the array are appended.
         */
        function mergeExecutions(incoming: Execution[]): void {
            const newItems: Execution[] = [];
            for (const updated of incoming) {
                if (!updated.id) continue;
                const existing = executionMap.get(updated.id);
                if (existing) {
                    // Update via reactive proxy so Vue detects the change.
                    Object.assign(existing, updated);
                } else {
                    newItems.push(updated);
                }
            }
            if (newItems.length) {
                const startIdx = executionHistory.executions.length;
                // Single reactive mutation instead of N individual pushes.
                executionHistory.executions.push(...newItems);
                // Store reactive proxies (obtained via array index access) so future
                // calls can update these items in-place without rebuilding the map.
                for (let i = startIdx; i < executionHistory.executions.length; i++) {
                    const e = executionHistory.executions[i];
                    if (e.id) executionMap.set(e.id, e);
                }
            } else {
                // Only in-place updates occurred — force computed re-evaluation
                // by replacing the array reference. Inner object references are
                // preserved (same reactive proxies), so executionMap remains valid.
                executionHistory.executions = [...executionHistory.executions]
            }
        }

        /**
         * Reconciles the store with a complete set of fresh API records.
         * - Updates existing items in-place (preserves reactive identity).
         * - Appends new items.
         * - Removes items that are no longer present in the API response.
         *
         * This should only be called once ALL pages have been collected so
         * the `freshRecords` set is complete.
         */
        function reconcileExecutions(freshRecords: Execution[]): void {
            const freshIds = new Set<string>();
            const newItems: Execution[] = [];

            for (const updated of freshRecords) {
                if (!updated.id) continue;
                freshIds.add(updated.id);
                const existing = executionMap.get(updated.id);
                if (existing) {
                    Object.assign(existing, updated);
                } else {
                    newItems.push(updated);
                }
            }

            // Remove items that are no longer in the API response
            const staleIds: string[] = [];
            for (const [id] of executionMap) {
                if (!freshIds.has(id)) {
                    staleIds.push(id);
                }
            }

            if (staleIds.length > 0) {
                const staleSet = new Set(staleIds);
                executionHistory.executions = executionHistory.executions.filter(
                    (x) => !x.id || !staleSet.has(x.id)
                );
                for (const id of staleIds) {
                    executionMap.delete(id);
                }
            }

            // Append new items
            if (newItems.length) {
                const startIdx = executionHistory.executions.length;
                executionHistory.executions.push(...newItems);
                for (let i = startIdx; i < executionHistory.executions.length; i++) {
                    const e = executionHistory.executions[i];
                    if (e.id) executionMap.set(e.id, e);
                }
            }

            // Force computed re-evaluation after in-place property updates
            executionHistory.executions = [...executionHistory.executions];
        }

        /**
         * Loads execution history.
         *
         * @param resetData - When `true` (default), clears all existing executions
         *   and shows the primary loading indicator before the first page arrives.
         *   Use `false` for background refreshes: existing records are updated
         *   in-place and new records are appended, so the table never flashes.
         *   Stale items (archived/deleted externally) are removed after all pages load.
         */
        async function loadExecutionHistory(resetData: boolean = true): Promise<void> {
            const loadId = ++activeLoadId;
            seenKeys.clear();
            executionHistory.paginationError = false;

            try {
                if (resetData) {
                    executionHistory.isLoading = true;
                    executionHistory.executions = [];
                } else {
                    executionHistory.isRefreshing = true;
                }

                const args = await buildArgs();
                if (loadId !== activeLoadId) {
                    return;
                }

                const response = await executionApiService.getExecutions(args);
                if (loadId !== activeLoadId) {
                    return;
                }

                const responseData: GetExecutionsResponse = response.data;
                const pageExecutions = responseData.executions ?? [];

                if (resetData) {
                    executionHistory.executions = pageExecutions;
                    // Rebuild the persistent map with reactive proxies from the new array.
                    executionMap.clear();
                    for (const e of executionHistory.executions) {
                        if (e.id) executionMap.set(e.id, e);
                    }

                    executionHistory.lastFetch = Date.now();

                    const nextKey = responseData.nextKey;
                    if (nextKey) {
                        executionHistory.isLoadingMore = true;
                        // Fire-and-forget for initial load: pages are appended as they arrive.
                        loadAdditionalPages(args, nextKey, loadId, false);
                    }
                } else {
                    // Background refresh: collect ALL pages, then reconcile in one pass
                    // so that externally archived/deleted items are removed.
                    const allRecords: Execution[] = [...pageExecutions];

                    const nextKey = responseData.nextKey;
                    if (nextKey) {
                        executionHistory.isLoadingMore = true;
                        const additionalRecords = await fetchAllRemainingPages(args, nextKey, loadId);
                        if (loadId !== activeLoadId) return;
                        allRecords.push(...additionalRecords);
                        executionHistory.isLoadingMore = false;
                    }

                    reconcileExecutions(allRecords);
                    executionHistory.lastFetch = Date.now();
                }

            } catch (e) {
                console.error('Error loading execution history:', e);
                executionHistory.hasLoadingError = true;
                toast.error('Failed to load analysis history');
            } finally {
                executionHistory.isLoading = false;
                executionHistory.isRefreshing = false;
            }
        }

        /**
         * Fetches all remaining pages and returns the collected records.
         * Used by the refresh path to gather the complete dataset before reconciliation.
         */
        async function fetchAllRemainingPages(
            baseArgs: GetExecutionsArgs,
            startKey: string,
            loadId: number,
        ): Promise<Execution[]> {
            const buffer: Execution[] = [];
            let currentKey: string = startKey;

            while (true) {
                if (loadId !== activeLoadId) return buffer;

                if (seenKeys.has(currentKey)) {
                    console.warn(`Pagination stopped: nextKey "${currentKey}" already seen (possible last-page echo).`);
                    break;
                }
                seenKeys.add(currentKey);

                baseArgs.startKey = currentKey;

                const response = await executionApiService.getExecutions(baseArgs);
                if (loadId !== activeLoadId) return buffer;

                const responseData: GetExecutionsResponse = response.data;
                const pageExecutions = responseData.executions ?? [];

                if (pageExecutions.length === 0) break;
                buffer.push(...pageExecutions);

                const nextKey = responseData.nextKey;
                if (!nextKey || nextKey === currentKey) break;
                currentKey = nextKey;
            }

            return buffer;
        }

        /**
         * Background-only: fetches additional pages starting from `startKey`.
         * When `merge` is true, records are upserted in-place (background refresh).
         * When `merge` is false, records are appended (initial load).
         */
        async function loadAdditionalPages(
            baseArgs: GetExecutionsArgs,
            startKey: string,
            loadId: number,
            merge: boolean = false
        ): Promise<void> {
            const buffer: Execution[] = [];
            let currentKey: string = startKey;

            try {
                // Iterative loop replaces prior recursion. All pages are collected into a
                // buffer before a single write to the reactive store, reducing computed-chain
                // re-evaluations from N (one per page) to 1. The already-built baseArgs object
                // is reused to avoid redundant auth.initialize() calls on every page fetch.
                while (true) {
                    if (loadId !== activeLoadId) return; // new load started; let it manage isLoadingMore

                    if (seenKeys.has(currentKey)) {
                        console.warn(`Pagination stopped: nextKey "${currentKey}" already seen (possible last-page echo).`);
                        break;
                    }
                    seenKeys.add(currentKey);

                    baseArgs.startKey = currentKey;

                    const response = await executionApiService.getExecutions(baseArgs);
                    if (loadId !== activeLoadId) return;

                    const responseData: GetExecutionsResponse = response.data;
                    const pageExecutions = responseData.executions ?? [];

                    if (pageExecutions.length === 0) break;
                    buffer.push(...pageExecutions);

                    const nextKey = responseData.nextKey;
                    if (!nextKey || nextKey === currentKey) break;
                    currentKey = nextKey;
                }

                if (loadId !== activeLoadId) return;

                if (buffer.length > 0) {
                    if (merge) {
                        // mergeExecutions keeps executionMap in sync internally.
                        mergeExecutions(buffer);
                    } else {
                        const startIdx = executionHistory.executions.length;
                        // Single reactive mutation for all buffered pages.
                        executionHistory.executions.push(...buffer);
                        for (let i = startIdx; i < executionHistory.executions.length; i++) {
                            const e = executionHistory.executions[i];
                            if (e.id) executionMap.set(e.id, e);
                        }
                    }
                }
            } catch (e) {
                console.error('Error loading additional execution history pages:', e);
                executionHistory.paginationError = true;
            }
            // Reached on normal completion or after catch — NOT on early loadId returns,
            // since those leave isLoadingMore for the new active load to manage.
            executionHistory.isLoadingMore = false;
        }

        /**
         * Satisfies the CacheStore interface used by refreshService.
         */
        function saveToCacheStore(_data?: any): void {
            //noop 
        }

        /**
         * Remove an execution and its subtree from the store.
         * If the target is the root, removes the entire tree (all executions sharing the rootId).
         * If the target is a child, removes only the target and its descendants via BFS on parentId.
         * Used after archiving to keep the UI in sync with the backend.
         */
        function removeByHierarchy(executionId: string): void {
            const target = executionHistory.executions.find((x) => x.id === executionId)
            const rootId = target?.rootId ?? executionId

            if (!target) {
                // Target not found — fall back to removing just the single executionId
                executionHistory.executions = executionHistory.executions.filter(
                    (x) => x.id !== executionId
                )
                executionMap.delete(executionId)
                return
            }

            if (executionId === rootId) {
                // Target IS the root — remove the entire tree (existing behavior)
                executionHistory.executions = executionHistory.executions.filter(
                    (x) => x.id !== executionId && x.rootId !== rootId
                )

                for (const [id, entry] of executionMap) {
                    if (id === executionId || entry.rootId === rootId) {
                        executionMap.delete(id)
                    }
                }
            } else {
                // Target IS a child — collect only target + descendants via BFS
                const idsToRemove = new Set<string>([executionId])
                const queue = [executionId]
                while (queue.length > 0) {
                    const current = queue.shift()!
                    for (const exec of executionHistory.executions) {
                        if (exec.parentId === current && exec.id && !idsToRemove.has(exec.id)) {
                            idsToRemove.add(exec.id)
                            queue.push(exec.id)
                        }
                    }
                }

                executionHistory.executions = executionHistory.executions.filter(
                    (x) => !x.id || !idsToRemove.has(x.id)
                )
                for (const id of idsToRemove) {
                    executionMap.delete(id)
                }
            }
        }

        return {
            executionHistory,
            resetStore,
            loadExecutionHistory,
            loadAdditionalPages,
            saveToCacheStore,
            removeByHierarchy,
        }
    })()
}

/**
 * Backward-compatible wrapper that returns the execution history store
 * configured for the NCAWorkflow type. Existing callers (Analysis History
 * page, refreshService, etc.) require zero changes.
 */
export const useExecutionHistoryStore = () =>
    createExecutionHistoryStore(
        ExecutionWorkflowType.NCAWorkflow,
        InjectionService.injectStrict(ExecutionApiService.injectableKey)
    )
